import { useCallback, useEffect, useMemo, useState } from 'react';
import { DollarSign, Banknote, Receipt, Package } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { StripeForeignEventsPage } from '@/pages/billing/StripeForeignEventsPage';
import { RevenuePage } from '@/pages/billing/RevenuePage';
import { PayoutsPage } from '@/pages/billing/PayoutsPage';
import { TenantBilling } from '@/pages/tenants/TenantBilling';
import { OrdersPage } from '@/pages/orders/OrdersPage';

/**
 * Money — one leaf, four tabs (Warren's ruling 3, 2026-09-24).
 *
 * The framework's operator menu has a single Money item: "Payouts,
 * revenue, subscription, invoices. Tabs, one item." Today those live as
 * three children of a Billing parent plus a stray Orders leaf under
 * Sales — four separate places to look for the same question.
 *
 * Each tab renders the EXISTING page unchanged, header and all. No
 * `embedded` prop threaded through four files, no duplicated data
 * fetching, and each page keeps working at its own URL for anything
 * that deep-links to it. The tab bar is the only new chrome.
 *
 * TRANSACTIONS IS GONE (2026-09-24). It was `/transactions`, which
 * renders <SessionsPage onNavigate={...} /> — the identical component
 * with identical props that `/sessions` renders. A tab here reproduced
 * the top-level Sessions leaf under a second name, which is the exact
 * friction this pass exists to remove. The route still works for old
 * links; it simply is not a tab and is not a leaf.
 *
 * SUBSCRIPTION IS GATED ON THE SAME TEST THE SERVER APPLIES, so an
 * operator sees three tabs and a tenant admin sees four, and no tab
 * 403s. The tab body is <TenantBilling>, the component Settings
 * already embeds — the surface exists and works; what it lacked was a
 * home an operator-tier menu could contain.
 *
 * ⚠️ WHY THIS IS A TIER TEST AND NOT A PERMISSION STRING, unlike the
 * Alerts leaf. Alerts got its own matrix resource because its routes
 * gated on a matrix bit. The four tenant-billing routes
 * (apps/api/src/routes/admin/tenant-billing.ts — GET .../billing,
 * setup-intent, subscription, switch-cadence) gate on
 * requireTenantScope instead, which admits super_admin and a
 * NON-TEMPLATED tenant_admin of that tenant, and refuses an operator.
 * That is already exactly the split the ruling asks for.
 *
 * Minting a `Subscription` resource would mean re-gating four live
 * tenant-billing routes to buy behaviour they already have. The
 * faithful reading of "capability-gated" is that the tab is driven by
 * whatever the server actually enforces rather than hardcoded — so it
 * mirrors requireTenantScope, and if that guard ever moves to a matrix
 * bit this predicate moves with it.
 */

type MoneyTab = 'revenue' | 'payouts' | 'subscription' | 'orders' | 'foreign-stripe';

const ALL_TABS: ReadonlyArray<{
  id: MoneyTab;
  label: string;
  icon: typeof DollarSign;
  /** True when this viewer may open the tab. Absent = always. */
  allow?: (u: { role?: string | null; template?: string | null } | null | undefined) => boolean;
}> = [
  { id: 'revenue', label: 'Revenue', icon: DollarSign },
  { id: 'payouts', label: 'Payouts', icon: Banknote },
  {
    id: 'subscription',
    label: 'Subscription',
    icon: Receipt,
    // requireTenantScope, mirrored: super_admin, or a tenant_admin
    // whose session is not a templated persona. A templated persona
    // presents a tenant_admin JWT claim it did not earn — the hole
    // authz.ts closes with isScopedPersona — so `template` must be
    // absent here for the same reason.
    allow: (u) => u?.role === 'super_admin'
      || (u?.role === 'tenant_admin' && !u?.template),
  },
  { id: 'orders', label: 'Orders', icon: Package },
  { id: 'foreign-stripe', label: 'Foreign Stripe events', icon: Receipt, allow: u => !u?.template && ['super_admin', 'operator'].includes(u?.role ?? '') },
];

function readTab(valid: ReadonlySet<string>): MoneyTab {
  const raw = new URLSearchParams(window.location.search).get('tab');
  return raw && valid.has(raw) ? (raw as MoneyTab) : 'revenue';
}

export function MoneyPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  const user = useAuthStore((s) => s.user);
  const tenantId = user?.tenant_id ?? null;
  const tabs = useMemo(
    () => ALL_TABS.filter((t) => !t.allow || t.allow(user)),
    [user],
  );
  // A hand-typed ?tab=subscription must not open a tab this viewer
  // cannot have — the valid set is theirs, not the full catalogue, so
  // an unauthorized deep link falls back to Revenue rather than
  // rendering a body whose every request 403s.
  const validForViewer = useMemo(() => new Set<string>(tabs.map((t) => t.id)), [tabs]);
  const [tab, setTab] = useState<MoneyTab>(() => readTab(validForViewer));

  // Back/forward should move between tabs, since each one is a URL.
  useEffect(() => {
    const onPop = (): void => setTab(readTab(validForViewer));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [validForViewer]);

  // replaceState, not pushState: flipping tabs should not bury the
  // page the user arrived from under four history entries.
  const select = useCallback((next: MoneyTab) => {
    setTab(next);
    const qs = next === 'revenue' ? '' : `?tab=${next}`;
    window.history.replaceState({}, '', `/money${qs}`);
  }, []);

  return (
    <div>
      <nav
        aria-label="Money tabs"
        style={{
          display: 'flex', gap: 4, padding: '0 0 4px',
          borderBottom: '1px solid var(--color-brd, var(--evi-border))',
          marginBottom: 4, flexWrap: 'wrap',
        }}
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => select(t.id)}
              aria-current={active ? 'page' : undefined}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', background: 'transparent', cursor: 'pointer',
                border: 'none',
                borderBottom: `2px solid ${active ? 'var(--evi-accent, #2ECC71)' : 'transparent'}`,
                color: active ? 'var(--color-tx)' : 'var(--color-tx-m)',
                fontSize: 13, fontWeight: active ? 600 : 400,
              }}
            >
              <Icon size={14} />
              <span>{t.label}</span>
            </button>
          );
        })}
      </nav>

      {tab === 'revenue' && <RevenuePage />}
      {tab === 'payouts' && <PayoutsPage />}
      {tab === 'subscription' && tenantId && <TenantBilling tenantId={tenantId} />}
      {tab === 'foreign-stripe' && <StripeForeignEventsPage />}
      {tab === 'orders' && <OrdersPage onNavigate={onNavigate} />}
    </div>
  );
}
